import type { ReactNode } from "react"
import { useEffect } from "react"
import { useAuth } from "../contexts/auth-context"
import { useNavigate } from "react-router-dom"
import { Loader2 } from "lucide-react"

type ProtectedRouteProps = {
  children: ReactNode;
  requiredRole: string | string[];
};

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()

  const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole]

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/") // redirect to home
      return
    }

    if (!isLoading && user) {
      // If a specific role is required and user doesn't have it, redirect
      if (requiredRole && !allowedRoles.includes(user.role)) {
        if (user.role === "PolicyUser") {
          navigate("/user/policy-search")
        } else {
          navigate("/user/dashboard")
        }
        return
      }
    }
  }, [user, isLoading, navigate, requiredRole])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!user) {
    return null
  }

  if (requiredRole && !allowedRoles.includes(user.role)) {
    return null
  }

  return <>{children}</>
}
